/**
 * Utility functions for session management and formatting
 */

export interface Session {
  id: string;
  user_id: string;
  title: string | null;
  created_at: string;
  last_active: string;
  is_active: boolean;
}

/**
 * Formats a date string into a readable format
 * @param dateString - ISO date string
 * @returns Formatted date string (e.g., "Oct 14, 2025, 2:30 PM")
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

/**
 * Gets a human-readable time ago string
 * @param dateString - ISO date string
 * @returns Time ago string (e.g., "2h ago", "3d ago", or formatted date if older)
 */
export const getTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
  
  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;
  
  return formatDate(dateString);
};

/**
 * Sorts sessions by last active date, most recent first
 * @param sessions - Array of sessions to sort
 * @returns Sorted array of sessions
 */
export const sortSessionsByLastActive = (sessions: Session[]): Session[] => {
  return sessions.sort((a, b) => 
    new Date(b.last_active).getTime() - new Date(a.last_active).getTime()
  );
};

/**
 * CSS styles for scrollbar that only shows when scrolling
 */
export const scrollbarStyles = `
  .scrollbar-on-scroll {
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;
    transition: scrollbar-color 0.3s ease;
  }
  
  .scrollbar-on-scroll:hover {
    scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
  }
  
  .scrollbar-on-scroll::-webkit-scrollbar {
    width: 6px;
  }
  
  .scrollbar-on-scroll::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .scrollbar-on-scroll::-webkit-scrollbar-thumb {
    background-color: transparent;
    border-radius: 3px;
    transition: background-color 0.3s ease;
  }
  
  .scrollbar-on-scroll:hover::-webkit-scrollbar-thumb {
    background-color: rgba(156, 163, 175, 0.5);
  }
  
  .scrollbar-on-scroll::-webkit-scrollbar-thumb:hover {
    background-color: rgba(156, 163, 175, 0.7);
  }
`;